import { useCallback, useEffect, useState } from 'react';

import { useDebouncedCallback } from '@/hooks/use-debounced-callback';

const SEARCH_DEBOUNCE_MS = 250;

interface UseDebouncedSearchFilterParams {
  /** The value currently committed to the filter state (URL). */
  committedValue: string;
  /** Minimum characters before a term is committed; shorter terms commit ''. */
  minCharacters?: number;
  /** Commits a (debounced, threshold-passing) value to the filter state. */
  onCommit: (value: string) => void;
}

interface UseDebouncedSearchFilterResult {
  /** Live input value — keeps typing responsive while commits are debounced. */
  inputValue: string;
  /** Wire to the input's onChange. */
  onInputChange: (value: string) => void;
}

/**
 * Decouples a search input's keystrokes from the committed filter value so that
 * each character doesn't trip the URL → query key → a backend request. Typing
 * updates local state immediately; the committed value (which drives queries) is
 * only updated after the debounce and once `minCharacters` is satisfied.
 *
 * Terms shorter than `minCharacters` commit `''` (clearing the applied filter)
 * while the input still shows what the user typed.
 */
export const useDebouncedSearchFilter = ({
  committedValue,
  minCharacters = 0,
  onCommit,
}: UseDebouncedSearchFilterParams): UseDebouncedSearchFilterResult => {
  const [inputValue, setInputValue] = useState(committedValue);

  // Re-sync when the committed value changes from outside this input
  // (e.g. clear-all, chip removal, restoring persisted filters).
  useEffect(() => {
    setInputValue(committedValue);
  }, [committedValue]);

  const commit = useDebouncedCallback((rawValue: string) => {
    const passesThreshold = rawValue.trim().length >= minCharacters;
    const nextValue = passesThreshold ? rawValue : '';
    if (nextValue !== committedValue) {
      onCommit(nextValue);
    }
  }, SEARCH_DEBOUNCE_MS);

  const onInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      commit(value);
    },
    [commit]
  );

  return { inputValue, onInputChange };
};
